import { Images } from '../assets'
import React, { useRef, useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import emailjs from '@emailjs/browser';
import ReCAPTCHA from "react-google-recaptcha";
import PageTitle from '../components/PageTitle'
import Tag from '../components/Tag'
import ImpactStat from '../components/ImpactStat'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Mousewheel } from 'swiper/modules'

const Placeholder = ({ label, className = '' }) => (
  <div className={`bg-gray-100 rounded-2xl flex items-center justify-center text-gray-400 text-sm ${className}`}>
    <span className="px-4 text-center">{label}</span>
  </div>
)

const SectionHeading = ({ children, className = '' }) => (
  <h1 className={`text-5xl font-bold text-[#2A2A2A] tracking-tight ${className}`}>{children}</h1>
)

const SectionSubtext = ({ children, className = '' }) => (
  <p className={`text-lg text-[#5B5B5B] leading-relaxed max-w-[640px] ${className}`}>{children}</p>
)

const projects = [
  { title: 'Clientshot', subtitle: 'Customer Satisfaction Tool', summary: 'Customer satisfaction tool helping hospitals gather and analyze patient feedback.', link: '/projects/clientshot-fct', image: Images.ClientshotMockup, bgColor: '#876ac4' },
  { title: 'BRIDGE', subtitle: 'Global Health Partnership', summary: 'Interoperable health data exchange platform connecting health facilities to national systems.', link: '/projects/bridge', image: Images.BridgeMockup, bgColor: '#F472B6' },
  { title: 'Cleft & Surgery E-Registry', subtitle: 'SmileTrain', summary: 'National cleft and surgery registry tracking over 3,000 patients across multiple states.', link: '/projects/cleft-surgery', image: Images.SmileTrainMockup, bgColor: '#1E3A5F' },
  { title: 'Ekiti PVT/LIMS', subtitle: 'Lab Information Management', summary: 'Laboratory information management and post-vaccination tracking system.', link: '/projects/ekiti', image: Images.EkitiHealthMockup, bgColor: '#A7F3D0' },
  { title: 'KNCV SMS/USSD', subtitle: 'TB Patient Engagement', summary: 'SMS and USSD platform for TB patient engagement and treatment adherence tracking.', link: '/projects/kncv', image: Images.KncvMockup, bgColor: '#FDE68A' },
]

const storyImages = [Images.EhrWhatwedo1, Images.EhrImg3122, Images.EhrDeployBottomLeft, Images.EhrStory4]

const HERO_SLIDES = [
  {
    type: 'video',
    src: Images.ClientshotDocumentary,
    duration: 10000,
    headline: 'Healthcare technology where it matters most',
    description: 'We design, build, and deploy digital health systems that work where infrastructure is limited, connectivity is unreliable, and the need is greatest.',
    primaryCta: { label: 'See Our Work', link: '/projects' },
    secondaryCta: { label: 'Work With Us', link: '/get-in-touch' },
  },
  {
    type: 'image',
    src: Images.EhrWhatwedo2,
    duration: 8000,
    headline: 'Breast cancer detection in communities without a hospital in sight.',
    description: 'We developed BRIDGE for the Bill and Melinda Gates Foundation and CHAI — a mobile tool that guides community health workers through structured breast health assessments in rural Nigeria, fully offline, where no connectivity exists.',
    primaryCta: { label: 'View our case studies', link: '/projects' },
    secondaryCta: { label: 'Start a conversation', link: '/get-in-touch' },
  },
  {
    type: 'image',
    src: Images.EhrWhatwedo3,
    duration: 8000,
    headline: 'Keeping TB patients in care — across 14 regions.',
    description: 'For the KNCV Tuberculosis Foundation, we implemented an SMS and USSD patient retention system across 14 TB LON regions in Nigeria. No smartphone required. No internet required. Just a reliable line between a patient and their care team.',
    primaryCta: { label: 'Browse our portfolio', link: '/projects' },
    secondaryCta: { label: 'Talk to our team', link: '/get-in-touch' },
  },
  {
    type: 'image',
    src: Images.SmiletrainGallery3,
    duration: 8000,
    headline: 'A national registry for every child born with a cleft in Nigeria.',
    description: 'We built a centralised web and mobile registry tracking 3,055 patients across all six geopolitical zones — giving SmileTrain and the Federal Ministry of Health real-time visibility into surgical care nationwide for the first time.',
    primaryCta: { label: 'Explore our projects', link: '/projects' },
    secondaryCta: { label: 'Get in touch', link: '/get-in-touch' },
  },
]

const services = [
  { title: 'Digital Health Systems', desc: 'Patient registries, civil registration systems, disease surveillance platforms, and health information systems built for national-scale deployment.', icon: Images.TechnologyIcon, color: '#60A5FA', link: '/services/digital-health-systems' },
  { title: 'Health Data & Analytics', desc: 'Data collection, monitoring, evaluation, reporting, and decision-support systems that turn health data into actionable insights.', icon: Images.DataIcon, color: '#34D399', link: '/services/health-data-analytics' },
  { title: 'Implementation & Advisory', desc: 'Technical advisory, deployment support, modernization, training, and long-term technical assistance for health programmes.', icon: Images.AssessmentIcon, color: '#A78BFA', link: '/services/implementation-advisory' },
]

const testimonials = [
  {
    quote: `ClientShot has given us deeper insight into what our clients are really experiencing. Whether feedback is collected through assisted sessions or completed independently, every voice is heard. Our facility administrators can review concerns promptly and follow up quickly, helping us improve the services we provide.`,
    attribution: 'Healthcare Worker, HIV Programme Facility',
  },
  {
    quote: `Since introducing ClientShot, we've seen an improvement in client satisfaction. Patients are more comfortable sharing their experiences and recommendations, giving us valuable feedback that helps us make meaningful improvements to our services.`,
    attribution: 'Facility Staff',
  },
  {
    quote: `ClientShot allows us to assess patients and capture their complaints or recommendations during the same visit. We can identify issues immediately instead of discovering them much later, allowing us to respond faster and improve the patient experience.`,
    attribution: 'Healthcare Worker',
  },
  {
    quote: `ClientShot helps us identify service delivery issues across different units within the facility and respond appropriately. Having timely patient feedback allows us to intervene earlier and make informed improvements where they are needed most.`,
    attribution: 'Programme / Facility Team Member',
  },
]

const STATIC_STYLES = {
  marqueeContent: { width: 'max-content' },
  featuredWorkGradient: {
    background: `radial-gradient(
      ellipse 80% 60% at bottom center,
      rgba(29, 143, 255, 0.12) 0%,
      rgba(42, 168, 255, 0.08) 25%,
      rgba(123, 200, 255, 0.04) 45%,
      transparent 70%
    )`,
  },
  projectImage: { objectPosition: 'center 30%' },
  serviceIcon: { opacity: 0.8 },
  projectBgs: projects.reduce((acc, p) => {
    acc[p.title] = { backgroundColor: p.bgColor }
    return acc
  }, {}),
  serviceBgs: services.reduce((acc, s) => {
    acc[s.title] = { backgroundColor: `${s.color}20` }
    return acc
  }, {}),
}

const sendEmailWithTimeout = (serviceId, templateId, templateParams, timeoutMs = 30000) => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject({ text: 'Request timed out. Please check your connection and try again.' })
    }, timeoutMs)

    emailjs.send(serviceId, templateId, templateParams)
      .then((result) => {
        clearTimeout(timeoutId)
        resolve(result)
      })
      .catch((err) => {
        clearTimeout(timeoutId)
        reject(err)
      })
  })
}

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
)

const Home = () => {
  const prevRef = useRef(null)
  const nextRef = useRef(null)
  const [modalName, setModalName] = useState("")
  const [modalOrganisation, setModalOrganisation] = useState("")
  const [modalEmail, setModalEmail] = useState("")
  const [modalMessage, setModalMessage] = useState("")
  const [modalLoader, setModalLoader] = useState(false)
  const [modalSuccess, setModalSuccess] = useState(false)
  const [modalError, setModalError] = useState(false)
  const [modalErrorMessage, setModalErrorMessage] = useState('')
  const modalRecaptcha = useRef(null)
  const [activeSlide, setActiveSlide] = useState(0)
  const [progress, setProgress] = useState(0)

  const staticStyles = useMemo(() => STATIC_STYLES, [])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    let rafId
    let lastTime = null
    const duration = HERO_SLIDES[activeSlide].duration

    const tick = (timestamp) => {
      if (lastTime === null) lastTime = timestamp
      const delta = timestamp - lastTime
      lastTime = timestamp

      setProgress((prev) => {
        const next = prev + delta / duration
        if (next >= 1) {
          setActiveSlide((current) => (current + 1) % HERO_SLIDES.length)
          return 0
        }
        return next
      })

      rafId = requestAnimationFrame(tick)
    }

    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [activeSlide])

  const goToSlide = (index) => {
    setActiveSlide(index)
    setProgress(0)
  }

  useEffect(() => {
    if (!process.env.REACT_APP_EMAILJS_KEY) {
      console.error('EmailJS public key is missing. Check your environment variables.')
      return
    }
    emailjs.init({
      publicKey: process.env.REACT_APP_EMAILJS_KEY,
      blockHeadless: true,
      limitRate: {
        id: 'app',
        throttle: 10000,
      },
    })
  }, [])

  const submitModalForm = async (e) => {
    e.preventDefault()
    setModalError(false)
    setModalErrorMessage('')

    if (!process.env.REACT_APP_SERVICEID || !process.env.REACT_APP_TEMPLATEID || !process.env.REACT_APP_EMAILJS_KEY) {
      setModalError(true)
      setModalErrorMessage('Email service is not configured. Please contact us directly at info@seamhealth.com.')
      return
    }

    if (!modalRecaptcha.current) {
      setModalError(true)
      setModalErrorMessage('reCAPTCHA has not loaded. Please refresh the page and try again.')
      return
    }

    const captchaValue = modalRecaptcha.current.getValue()
    if (!captchaValue) {
      setModalError(true)
      setModalErrorMessage('Please verify the reCAPTCHA.')
      return
    }

    setModalLoader(true)

    try {
      const templateParams = {
        name: modalName,
        organisation: modalOrganisation,
        email: modalEmail,
        messageBody: modalMessage,
      }
      const result = await sendEmailWithTimeout(process.env.REACT_APP_SERVICEID, process.env.REACT_APP_TEMPLATEID, templateParams)
      console.info('EmailJS send succeeded:', result)
      setModalName("")
      setModalOrganisation("")
      setModalEmail("")
      setModalMessage("")
      setModalSuccess(true)
      setModalError(false)
      modalRecaptcha.current.reset()
    } catch (err) {
      console.error('EmailJS send failed:', err)
      setModalError(true)
      setModalErrorMessage(err?.text || 'Something went wrong. Please try again or email us directly at info@seamhealth.com.')
      modalRecaptcha.current.reset()
    } finally {
      setModalLoader(false)
    }
  }

  return (
    <>
      <PageTitle title="Home | SeamHealth - Digital Health Implementation Partner" />

      {/* Section 1 - Hero: carousel background with content */}
      <section className="relative min-h-screen overflow-hidden flex items-center">
        <style>{`
          @keyframes heroSlideInRight {
            from { opacity: 0; transform: translateX(40px); }
            to { opacity: 1; transform: translateX(0); }
          }
          .animate-hero-slide-in {
            animation: heroSlideInRight 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
          }
          @keyframes heroZoom {
            from { transform: scale(1); }
            to { transform: scale(1.12); }
          }
          .animate-hero-zoom {
            animation: heroZoom 10s linear forwards;
          }
        `}</style>

        {HERO_SLIDES.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${activeSlide === i ? 'opacity-100 z-[1]' : 'opacity-0 z-0'}`}
          >
            {slide.type === 'video' ? (
              <video
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                controls={false}
                disablePictureInPicture
              >
                <source src={slide.src} type="video/mp4" />
              </video>
            ) : (
              <img decoding="async" src={slide.src} alt="" className={`absolute inset-0 w-full h-full object-cover ${activeSlide === i ? 'animate-hero-zoom' : ''}`} loading="eager" />
            )}
          </div>
        ))}

        <div className="absolute inset-0 z-[2] bg-black/50" />

        <div className="relative z-20 w-full max-w-[1280px] mx-auto px-8 md:px-12">
          <div key={activeSlide} className="max-w-[640px] animate-hero-slide-in">
            {HERO_SLIDES[activeSlide].eyebrow && (
              <p className="text-sm uppercase tracking-[0.15em] text-white/70 mb-4">
                {HERO_SLIDES[activeSlide].eyebrow}
              </p>
            )}
            <h1 className="text-5xl font-bold text-white">
              {HERO_SLIDES[activeSlide].headline}
            </h1>
            <p className="body text-white/70 mt-6 leading-relaxed max-w-[580px]">
              {HERO_SLIDES[activeSlide].description}
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to={HERO_SLIDES[activeSlide].primaryCta.link} className="btn btn-primary">
                {HERO_SLIDES[activeSlide].primaryCta.label} <img decoding="async" src={Images.ArrowUpRight} alt="Arrow icon" loading="lazy" />
              </Link>
              <Link to={HERO_SLIDES[activeSlide].secondaryCta.link} className="btn btn-secondary text-white">
                {HERO_SLIDES[activeSlide].secondaryCta.label} <img decoding="async" src={Images.ArrowUpRight} alt="Arrow icon" loading="lazy" />
              </Link>
            </div>
          </div>
        </div>

        {/* Carousel dots & progress loader */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
          {HERO_SLIDES.map((slide, i) => {
            const isActive = activeSlide === i
            const circumference = 2 * Math.PI * 10
            const offset = circumference * (1 - (isActive ? progress : 0))
            return (
              <button
                key={i}
                type="button"
                onClick={() => goToSlide(i)}
                className="relative w-6 h-6 flex items-center justify-center rounded-full focus:outline-none"
                aria-label={`Go to slide ${i + 1}`}
              >
                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" className="text-white/20" />
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    className={`transition-all duration-100 ease-linear ${isActive ? 'text-white' : 'text-transparent'}`}
                  />
                </svg>
                <span className={`w-2 h-2 rounded-full transition-colors duration-300 ${isActive ? 'bg-white' : 'bg-white/40'}`} />
              </button>
            )
          })}
        </div>

        <a href="#impact-section" className="absolute bottom-8 left-8 md:left-12 z-20">
          <div className="scroll-cta flex gap-3 items-center whitespace-nowrap">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white/50 animate-float">
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
            <p className="text text-white/50 hidden md:block">Scroll to explore</p>
          </div>
        </a>
      </section>

      {/* Section 2 - Trust Bar */}
      <section id="impact-section" className="py-14 md:py-16 bg-white">
        <div className="max-w-[1280px] mx-auto px-8 md:px-12">
          <h4 className="impact-heading text-center uppercase tracking-[0.15em] text-[#7E7E7E] mb-8">
            Our reach across Sub-Saharan Africa
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            <ImpactStat number="143,000+" label="Users Engaged" index={0} />
            <ImpactStat number="249" label="Health Facilities Onboarded" index={1} />
            <ImpactStat number="9+" label="Institutional Clients" index={2} />
            <ImpactStat number="2" label="Peer-Reviewed Publications" index={3} />
          </div>
          <div className="text-center mt-[9.88rem] md:mt-[10.99rem]">
            <SectionHeading>Trusted By Leading Global Health Organizations</SectionHeading>
          </div>
        </div>
        <div className="marquee-track relative mt-10">
          <div className="absolute inset-y-0 left-0 w-20 md:w-32 z-10 pointer-events-none bg-gradient-to-r from-white to-transparent" />
          <div className="absolute inset-y-0 right-0 w-20 md:w-32 z-10 pointer-events-none bg-gradient-to-l from-white to-transparent" />
          <div className="marquee-content flex items-center gap-5" style={staticStyles.marqueeContent}>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.UnicefLogo} alt="UNICEF" className="max-h-12 md:max-h-14 max-w-[120px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.GatesLogo} alt="Bill &amp; Melinda Gates Foundation" className="max-h-12 md:max-h-14 max-w-[140px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.ChaiLogo} alt="Clinton Health Access Initiative (CHAI)" className="max-h-12 md:max-h-14 max-w-[140px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.IhvnNigeria} alt="Institute of Human Virology Nigeria (IHVN)" className="max-h-12 md:max-h-14 max-w-[140px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.SmileTrainLogo} alt="Smile Train" className="max-h-12 md:max-h-14 max-w-[140px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.GlobalFundLogo} alt="The Global Fund" className="max-h-12 md:max-h-14 max-w-[140px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.FmohLogo} alt="Federal Ministry of Health and Social Welfare, Nigeria" className="max-h-12 md:max-h-14 max-w-[160px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.EkitiClientLogo} alt="Ekiti State Ministry of Health" className="max-h-12 md:max-h-14 max-w-[120px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.KncvNigeria} alt="KNCV Tuberculosis Foundation" className="max-h-12 md:max-h-14 max-w-[140px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.UnicefLogo} alt="UNICEF" className="max-h-12 md:max-h-14 max-w-[120px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.GatesLogo} alt="Bill &amp; Melinda Gates Foundation" className="max-h-12 md:max-h-14 max-w-[140px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.ChaiLogo} alt="Clinton Health Access Initiative (CHAI)" className="max-h-12 md:max-h-14 max-w-[140px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.IhvnNigeria} alt="Institute of Human Virology Nigeria (IHVN)" className="max-h-12 md:max-h-14 max-w-[140px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.SmileTrainLogo} alt="Smile Train" className="max-h-12 md:max-h-14 max-w-[140px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.GlobalFundLogo} alt="The Global Fund" className="max-h-12 md:max-h-14 max-w-[140px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.FmohLogo} alt="Federal Ministry of Health and Social Welfare, Nigeria" className="max-h-12 md:max-h-14 max-w-[160px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.EkitiClientLogo} alt="Ekiti State Ministry of Health" className="max-h-12 md:max-h-14 max-w-[120px] object-contain" loading="lazy" />
            </div>
            <div className="flex items-center justify-center h-28 md:h-32 w-44 md:w-52 shrink-0 rounded-xl border border-[#E6E6E6] bg-[#FAFAFA]">
              <img decoding="async" src={Images.KncvNigeria} alt="KNCV Tuberculosis Foundation" className="max-h-12 md:max-h-14 max-w-[140px] object-contain" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 - The Story */}
      <section className="py-40 md:py-48 bg-[#F8F9FB] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] -translate-y-1/3 translate-x-1/4 rounded-full bg-[#007DD6]/5 blur-3xl pointer-events-none" />
        <div className="max-w-[1440px] mx-auto px-8 md:px-16">
          <div className="grid md:grid-cols-[1fr_1.35fr] gap-12 md:gap-20 items-center">
            <div data-aos="fade-up" className="py-8 md:py-12">
              <h1 className="text-5xl font-bold text-[#2A2A2A] tracking-tight leading-tight">
                Built for the places others do not reach.
              </h1>
              <p className="text-[#5B5B5B] mt-8 leading-relaxed max-w-[540px]">
                Most digital health innovation is designed for the best-case scenario &mdash; reliable internet, modern devices, trained staff, functioning supply chains. We design for the other scenario. Our systems are built offline-first, because connectivity in a rural health post in Nasarawa State or a community clinic in Katsina is not guaranteed. Our interfaces are built for health workers who are busy, not techies who are comfortable. Our deployments happen in the field, not in a boardroom.
              </p>
              <blockquote className="border-l-[5px] border-[#007DD6] bg-[#007DD6]/5 pl-7 py-6 pr-6 my-10 rounded-xl">
                <p className="text-[#5B5B5B] leading-relaxed italic">
                  &ldquo;The communities that need digital health the most are exactly the ones that most providers find too difficult to reach. That is where we work.&rdquo;
                </p>
              </blockquote>
              <p className="text-[#5B5B5B] leading-relaxed">
                We are a US-incorporated organisation &mdash; which means we meet the governance, compliance, and partnership standards that bilateral donors and international organisations require. But our roots, our work, and our commitment are in Africa. Every system we have built has been deployed on African soil, in African communities, by African health workers. We do not parachute in and leave. We stay.
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 text-[#007DD6] font-medium mt-10 transition-all duration-300 group/link hover:text-[#007DD6]">
                Read about our approach
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-300 group-hover/link:translate-x-1">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            <div className="flex items-center py-8 md:py-12" data-aos="fade-left">
              <div className="grid grid-cols-1 gap-4 w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="group relative w-full h-[175px] md:h-[175px] rounded-xl overflow-hidden shadow-lg transition-all duration-500 hover:shadow-2xl hover:-translate-y-0.5">
                    <img
                      src={storyImages[0]}
                      alt="EHR deployment 1"
                      className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
                  </div>
                  <div className="group relative w-full h-[175px] md:h-[175px] rounded-xl overflow-hidden shadow-lg transition-all duration-500 hover:shadow-2xl hover:-translate-y-0.5">
                    <img
                      src={storyImages[1]}
                      alt="EHR deployment 2"
                      className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,0.3fr)_minmax(0,0.7fr)] gap-4">
                  <div className="group relative w-full h-[270px] md:h-[270px] rounded-xl overflow-hidden shadow-lg transition-all duration-500 hover:shadow-2xl hover:-translate-y-0.5">
                    <img
                      src={storyImages[2]}
                      alt="EHR deployment 3"
                      className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
                  </div>
                  <div className="group relative w-full h-[270px] md:h-[270px] rounded-xl overflow-hidden shadow-lg transition-all duration-500 hover:shadow-2xl hover:-translate-y-0.5">
                    <img
                      src={storyImages[3]}
                      alt="EHR deployment 4"
                      className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 - Featured Work */}
      <section className="py-16 md:py-20 bg-white relative">
        <div
          className="absolute inset-0 pointer-events-none"
          style={staticStyles.featuredWorkGradient}
        />
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 md:px-12">
          {/* ── Header row: heading + arrows ── */}
          <div className="flex items-end justify-between mb-20 gap-8">
            <div className="max-w-[640px]">
              <SectionHeading>Impact Through Technology</SectionHeading>
              <SectionSubtext className="mt-4">
                A selection of digital health systems delivered for governments, global health organizations, and national programmes.
              </SectionSubtext>
            </div>
            <div className="hidden sm:flex gap-2 shrink-0">
              <button ref={prevRef}
                className="project-prev w-10 h-10 rounded-full border border-[#D0D0D0] flex items-center justify-center text-[#5B5B5B] hover:border-[#007DD6] hover:text-[#007DD6] transition-all disabled:opacity-30"
                aria-label="Previous projects">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button ref={nextRef}
                className="project-next w-10 h-10 rounded-full border border-[#D0D0D0] flex items-center justify-center text-[#5B5B5B] hover:border-[#007DD6] hover:text-[#007DD6] transition-all disabled:opacity-30"
                aria-label="Next projects">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>

          {/* ── Carousel ── */}
          <Swiper
            modules={[Navigation, Mousewheel]}
            spaceBetween={24}
            slidesPerView={1}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            mousewheel={{ forceToAxis: true, releaseOnEdges: true, sensitivity: 1 }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl = prevRef.current
              swiper.params.navigation.nextEl = nextRef.current
              swiper.navigation.init()
              swiper.navigation.update()
            }}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="!overflow-visible [&_.swiper-slide]:!h-auto [&_.swiper-slide]:flex touch-pan-y"
          >
            {projects.map((p) => (
              <SwiperSlide key={p.title}>
                <Link to={p.link} className="group/card flex-1">
                  <div className="relative rounded-xl h-full">
                    <div className="relative rounded-[inherit] bg-white border border-[#E6E6E6] shadow-[0_8px_30px_rgba(0,0,0,0.08)] overflow-hidden transition-all duration-300 group-hover/card:border-[#007DD6] group-hover/card:-translate-y-1.5 group-hover/card:shadow-[0_10px_30px_rgba(0,0,0,0.12)] flex flex-col h-full group-hover/card:[animation:hoverBlurReveal_0.35s_ease]">
                      <div style={staticStyles.projectBgs[p.title]}>
                        {p.image ? (
                          <img decoding="async" src={p.image} alt={p.title} className="w-full aspect-[16/10] object-cover rounded-none shrink-0" style={staticStyles.projectImage} loading="lazy" />
                        ) : (
                          <Placeholder label="Project Screenshot" className="w-full aspect-[16/10] rounded-none shrink-0" />
                        )}
                      </div>
                      <div className="p-6 md:p-8 flex flex-col flex-1">
                        <span className="text-sm text-[#7E7E7E]">{p.subtitle}</span>
                        <h3 className="text-lg font-semibold text-[#2A2A2A] mt-1">{p.title}</h3>
                        <p className="text-[#5B5B5B] mt-2 text-sm leading-relaxed">{p.summary}</p>
                        <div className="mt-auto">
                          <span className="inline-flex items-center gap-2 text-[#007DD6] text-sm mt-4 shrink-0">
                            View Case Study <img decoding="async" src={Images.ArrowUpRight} alt="Arrow icon" className="w-3.5 h-3.5" loading="lazy" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* ── View All CTA ── */}
          <div className="text-center mt-14">
            <Link to="/projects" className="inline-flex items-center gap-2 text-[#007DD6] font-medium hover:gap-3 transition-all group/link">
              View All Projects
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#007DD6] transition-transform group-hover/link:translate-x-1">
                <path d="M5 12h14" />
                <path d="M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 4b - Testimonials */}
      <section className="py-40 md:py-48 bg-[#F3F8FF] relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] -translate-y-1/2 rounded-full bg-[#007DD6]/10 blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-[1000px] mx-auto px-8 md:px-12">
          <div className="text-center max-w-[720px] mx-auto mb-16" data-aos="fade-up">
            <span className="inline-block uppercase tracking-[0.15em] text-[#007DD6] text-sm font-medium mb-4">
              Trusted by Healthcare Teams
            </span>
            <SectionHeading>Trusted by Healthcare Teams Improving Patient Care</SectionHeading>
            <SectionSubtext className="mt-5 mx-auto">
              Healthcare workers are using ClientShot to capture actionable patient feedback, strengthen accountability, and continuously improve service delivery.
            </SectionSubtext>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {testimonials.map((t, i) => {
              const dark = i === 0
              return (
                <figure
                  key={i}
                  data-aos="fade-up"
                  data-aos-delay={i * 150}
                  className={`flex flex-col h-full rounded-2xl p-8 md:p-10 transition-all duration-300 hover:-translate-y-1.5 motion-reduce:transform-none motion-reduce:transition-none ${
                    dark
                      ? 'bg-[#0A3D6E] border border-[#0A3D6E] shadow-[0_4px_20px_rgba(0,0,0,0.12)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.25)]'
                      : 'bg-white border border-[#E6E6E6] shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.12)]'
                  }`}
                >
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="currentColor" className={`mb-6 ${dark ? 'text-white/20' : 'text-[#007DD6]/30'}`} aria-hidden="true">
                    <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.571-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-3.983v-10h9.983z" />
                  </svg>
                  <blockquote className={`text-base md:text-lg leading-relaxed flex-1 ${dark ? 'text-white' : 'text-[#2A2A2A]'}`}>
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <figcaption className={`mt-8 pt-6 border-t ${dark ? 'border-white/10' : 'border-[#E6E6E6]'}`}>
                    <span className={`block text-sm font-semibold ${dark ? 'text-[#7FB6E8]' : 'text-[#007DD6]'}`}>{t.attribution}</span>
                  </figcaption>
                </figure>
              )
            })}
          </div>

          <div className="text-center mt-20" data-aos="fade-up">
            <SectionHeading>Ready to strengthen patient experience in your facilities?</SectionHeading>
            <p className="text-[#5B5B5B] mt-4 leading-relaxed max-w-[560px] mx-auto">
              See how ClientShot helps healthcare programmes collect actionable patient feedback, resolve issues faster, and continuously improve service quality.
            </p>
            <Link to="/get-in-touch" className="btn btn-primary mt-8 w-fit mx-auto">
              Book a Demo <img decoding="async" src={Images.ArrowUpRight} alt="Arrow icon" loading="lazy" />
            </Link>
          </div>
        </div>
      </section>

      {/* Section 5 - Services Teaser */}
      <section className="py-40 md:py-48 bg-[#001a33] relative overflow-hidden">
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 md:px-12">
          <div className="text-center max-w-[640px] mx-auto mb-12">
            <SectionHeading className="text-white">What We Do</SectionHeading>
            <p className="text-white/50 mt-4 leading-relaxed">
              We work across the full arc of a digital health programme &mdash; from system design and field deployment to long-term advisory and scale-up.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {services.map((s, i) => (
              <Link
                key={i}
                to={s.link}
                className="group relative rounded-xl bg-white/5 border border-white/10 p-6 md:p-8 transition-all duration-300 hover:bg-white/10 hover:border-white/20 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/20"
                data-aos="fade-up"
                data-aos-delay={i * 100}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={staticStyles.serviceBgs[s.title]}>
                  <img decoding="async" src={s.icon} alt={`${s.title} icon`} className="w-6 h-6" style={staticStyles.serviceIcon} loading="lazy" />
                </div>
                <h3 className="text-lg font-semibold text-white">{s.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed mt-2">{s.desc}</p>
                <div className="flex items-center gap-2 text-white/40 text-sm font-medium mt-6 transition-all group-hover:text-white/80 group-hover:gap-3">
                  Learn more
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 17l9.2-9.2M17 17V7H7" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6 - INTEREST 2025 */}
      <section className="py-40 md:py-48 bg-[#E8F4F0] relative overflow-hidden">
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div data-aos="fade-up">
              <div className="mb-4">
                <Tag title="INTEREST 2025 &mdash; Windhoek, Namibia &mdash; May 2025" />
              </div>
              <SectionHeading>Our findings. The global stage.</SectionHeading>
              <SectionSubtext className="mt-4">
                We presented our implementation research on Clientshot and BRIDGE at INTEREST 2025 in Windhoek, Namibia, contributing to the global conversation on digital health in Africa.
              </SectionSubtext>
              <div className="mt-6 flex flex-wrap gap-2">
                <Tag title="INTEREST 2025" />
                <Tag title="Peer-Reviewed" />
              </div>
              <p className="text-[#5B5B5B] mt-4">
                Two peer-reviewed research presentations at INTEREST 2025.
              </p>
              <Link to="/publications" className="btn btn-primary mt-8 w-max">
                View Publications <img decoding="async" src={Images.ArrowUpRight} alt="Arrow icon" loading="lazy" />
              </Link>
            </div>
            <div data-aos="fade-left">
              <div className="group rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100 border border-[#E6E6E6]">
                <img decoding="async" src={Images.InterestConference} alt="SeamHealth team at INTEREST 2025" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7 - Contact CTA */}
      <section className="relative py-40 md:py-48 bg-gradient-to-br from-[#001630] to-[#002A4D] overflow-hidden">
        <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover z-[1] opacity-[0.2]">
          <source src={Images.ProjectVid} type="video/mp4" />
        </video>
        <div className="absolute inset-0 z-[2] bg-gradient-to-br from-[#001630]/60 to-[#002A4D]/60" />
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            <div data-aos="fade-up">
              <h1 className="text-5xl font-bold text-white tracking-tight">
                Working in communities that others overlook?
              </h1>
              <p className="text-lg text-white/70 mt-4 leading-relaxed">
                Tell us about your programme, your context, and what you are trying to achieve. We respond to every message personally within 48 hours.
              </p>
            </div>
            <div data-aos="fade-up" data-aos-delay={100} className="bg-white rounded-2xl p-8 md:p-10 shadow-xl">
              {modalSuccess ? (
                <div className="p-4 rounded-lg text-center text-green-700 bg-green-200">
                  Thank you. We have received your message and will be in touch within two business days.
                </div>
              ) : (
                <form onSubmit={submitModalForm}>
                  {modalError && (
                    <div className="mb-4 p-3 rounded-lg text-center text-red-700 bg-red-200 text-sm">
                      {modalErrorMessage}
                    </div>
                  )}
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-[#2A2A2A] mb-1.5">Full name</label>
                      <input value={modalName} type="text" onChange={(e) => setModalName(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-[#D0D0D0] text-sm focus:outline-none focus:border-[#007DD6] focus:ring-1 focus:ring-[#007DD6]/20 transition" placeholder="Your full name" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2A2A2A] mb-1.5">Organisation</label>
                      <input value={modalOrganisation} type="text" onChange={(e) => setModalOrganisation(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-[#D0D0D0] text-sm focus:outline-none focus:border-[#007DD6] focus:ring-1 focus:ring-[#007DD6]/20 transition" placeholder="Your organisation or institution" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2A2A2A] mb-1.5">Email address</label>
                      <input value={modalEmail} type="email" onChange={(e) => setModalEmail(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-[#D0D0D0] text-sm focus:outline-none focus:border-[#007DD6] focus:ring-1 focus:ring-[#007DD6]/20 transition" placeholder="your@email.com" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2A2A2A] mb-1.5">What are you working on?</label>
                      <textarea value={modalMessage} onChange={(e) => setModalMessage(e.target.value)} rows={4} className="w-full px-4 py-3 rounded-lg border border-[#D0D0D0] text-sm focus:outline-none focus:border-[#007DD6] focus:ring-1 focus:ring-[#007DD6]/20 transition resize-none" placeholder="Tell us about your programme, your challenge, or what you are looking for." required />
                    </div>
                    <ReCAPTCHA sitekey={process.env.REACT_APP_SITE_KEY} ref={modalRecaptcha} />
                    <button type="submit" disabled={modalLoader} className="btn btn-primary w-full text-center justify-center">
                      {modalLoader ? (
                        <span className='inline-flex items-center gap-2'>
                          <Spinner /> Sending...
                        </span>
                      ) : 'Send message'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Home
